import { useCallback, useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { AccessGrant, ProjectFolder, Space, UserGroup } from '@vidmakr/bindings/types';
import { accessAtLeast, accessFromOrgRole, type AccessRole } from '@vidmakr/shared';
import { useOrg } from './hooks';
import { reducers } from './stdb';
import { attempt } from './toast';
import { plural } from './selection';

/** The General space: projects and media with spaceId ''. */
export const GENERAL = 'general';

export type TargetKind = 'space' | 'folder' | 'asset_folder';

/**
 * Spaces, project folders, groups and grants of an organization (as far as
 * the viewer can see them), plus the viewer's access in any scope.
 */
export function useSpaces(orgId: string | undefined) {
  const id = orgId ?? '';
  const { role, members, org, ready: orgReady } = useOrg(orgId);
  const [spaceRows, spacesReady] = useTable(tables.space.where((s) => s.orgId.eq(id)));
  const [folderRows, foldersReady] = useTable(tables.projectFolder.where((f) => f.orgId.eq(id)));
  const [groupRows] = useTable(tables.userGroup.where((g) => g.orgId.eq(id)));
  const [groupMembers] = useTable(tables.groupMember.where((g) => g.orgId.eq(id)));
  const [grantRows] = useTable(tables.accessGrant.where((g) => g.orgId.eq(id)));
  const [mine] = useTable(tables.scopeAccess.where((s) => s.orgId.eq(id)));

  const spaces = useMemo(() => [...spaceRows].sort((a, b) => a.name.localeCompare(b.name)), [spaceRows]);
  const folders = useMemo(() => [...folderRows].sort((a, b) => a.name.localeCompare(b.name)), [folderRows]);
  const spaceById = useMemo(() => new Map(spaceRows.map((s) => [s.id, s])), [spaceRows]);
  const folderById = useMemo(() => new Map(folderRows.map((f) => [f.id, f])), [folderRows]);
  const myScopes = useMemo(() => new Map(mine.map((r) => [r.scopeId, r.role as AccessRole])), [mine]);

  /** The viewer's access in a scope ('' = open to the organization). */
  const scopeRole = useCallback(
    (scopeId: string): AccessRole | null => {
      const org = accessFromOrgRole(role ?? '');
      if (org === 'admin') return 'admin';
      if (!scopeId) return org;
      return myScopes.get(scopeId) ?? null;
    },
    [role, myScopes],
  );
  const canIn = useCallback((scopeId: string, min: AccessRole) => accessAtLeast(scopeRole(scopeId), min), [scopeRole]);

  /** Folders from the top of the space down to this one. */
  const folderPath = useCallback(
    (folderId: string): ProjectFolder[] => {
      const out: ProjectFolder[] = [];
      for (let f = folderById.get(folderId), i = 0; f && i < 64; f = folderById.get(f.parentId), i++) out.unshift(f);
      return out;
    },
    [folderById],
  );

  return {
    org,
    role,
    members,
    spaces,
    folders,
    spaceById,
    folderById,
    groups: groupRows as readonly UserGroup[],
    groupMembers,
    grants: grantRows as readonly AccessGrant[],
    scopeRole,
    canIn,
    folderPath,
    ready: orgReady && spacesReady && foldersReady,
  };
}

/** The scope of a place: a folder's, a space's, or '' for the General space's top level. */
export function placeScope(spaceById: Map<string, Space>, folderById: Map<string, ProjectFolder>, spaceId: string, folderId: string): string {
  if (folderId) return folderById.get(folderId)?.scopeId ?? '';
  return spaceId ? (spaceById.get(spaceId)?.scopeId ?? '') : '';
}

/** URL of a place in the project browser. */
export function placeUrl(orgId: string, spaceId: string, folderId = '') {
  const base = `/orgs/${orgId}/s/${spaceId || GENERAL}`;
  return folderId ? `${base}/f/${folderId}` : base;
}

export const SPACE_COLORS = ['#7c5cff', '#22d3ee', '#22c55e', '#eab308', '#f97316', '#ef4444', '#ec4899', '#64748b'];

/** Drag data for projects (`p:<id>`) and project folders (`f:<id>`). */
export const PROJECT_DRAG = 'application/x-vidmakr-projects';

/** Moves projects and project folders (selection keys) to a space ('' = General) or one of its folders. */
export function moveProjectItems(keys: string[], dest: { spaceId: string; folderId: string }) {
  return attempt(async () => {
    for (const k of keys) {
      if (k.startsWith('p:')) await reducers().moveProject({ projectId: k.slice(2), spaceId: dest.spaceId, folderId: dest.folderId });
      else if (k.startsWith('f:') && k.slice(2) !== dest.folderId) await reducers().moveProjectFolder({ id: k.slice(2), spaceId: dest.spaceId, parentId: dest.folderId });
    }
  }, `Moved ${plural(keys.length, 'item')}`);
}
